import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Minus, MousePointer, Plus, Rotate3d, RotateCcw, Search, X } from "lucide-react";

import {
  axisFor,
  easeOutCubic,
  interactionFor,
  offsetFromDrag,
  RETURN_MS,
  type Axis,
} from "./figure-interaction";
import { FigureChart } from "./figure-chart";
import { FigurePoses } from "./figure-poses";

export interface ZoomedFigure {
  src: string;
  alt: string;
}

/** What a figure is opened at: the whole card, however small the drawing is. */
const FIT = 1;
const MAX_SCALE = 6;
const STEP = 0.5;
const WHEEL_FACTOR = 1.15;
/** Double-tap / double-click lands here — big enough to read a millimetre mark. */
const DOUBLE_TAP_SCALE = 2.5;
/** How far a pointer may travel and still count as a tap rather than a drag. */
const TAP_SLOP = 8;
/** How long a tap waits for its partner before it is taken as a single tap. */
const DOUBLE_TAP_MS = 260;
/** A downward drag this far, at fit, is a dismiss — the phone gesture for "away". */
const SWIPE_CLOSE = 90;
/** Pinching in by this much, already at fit, is the same intent as scrolling out. */
const PINCH_CLOSE = 0.7;
/** Every pose of a turnable solid is rendered onto this square, in SVG units.
    Fixed, because a solid's silhouette is narrower at 45° than at 0° and a canvas
    sized to its own content would make the drawing jump sideways as it turns. */
const POSE_CANVAS = 420;
/** The loupe: a 3× window, 78px across. Small enough to see around. */
const LOUPE_R = 78;
const LOUPE_ZOOM = 3;

interface View {
  scale: number;
  x: number;
  y: number;
}

const FITTED: View = { scale: FIT, x: 0, y: 0 };

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * A figure from a question, opened larger in a modal over the page.
 *
 * **A card on the page, not a screen of its own.** It was a full-bleed black
 * viewport with a toolbar across the top and a line of help along the bottom,
 * which is the shape of a *page*: a child who opened a figure mid-question read
 * it as having left the question, and the way back looked like navigation. The
 * figure now sits in a panel with the question dimmed but still visibly *there*
 * behind it, so enlarging a drawing is plainly a thing that happened to the
 * question rather than a place it went.
 *
 * Why a modal rather than a hover magnifier: hover does not exist on the tablets
 * and phones this is mostly read on, and a magnifier that grows the figure in
 * place covers the sentence the figure belongs to. Tapping is the one gesture
 * that works everywhere, and the figures are SVG, so the panel is lossless — a 1°
 * protractor tick becomes genuinely readable rather than merely bigger.
 *
 * Fit is the floor, not the ceiling: the wheel, the buttons, a pinch and a
 * double tap go up to 6× beyond it, for the raster photos (coins, notes) where
 * fit-to-screen is still small on a phone.
 *
 * **Getting out is never a hunt for the X.** A figure is opened mid-question, so
 * every ordinary way of saying "done" closes it: Esc, a tap anywhere, a scroll
 * or pinch back out past fit, a downward swipe. The X stays because it is the
 * one affordance that is *visible*, but nothing depends on reaching it — an
 * enlarged figure that has to be dismissed precisely is a trap between a child
 * and the question they were answering.
 */
export function FigureLightbox({
  figure,
  onClose,
}: {
  figure: ZoomedFigure | null;
  onClose: () => void;
}) {
  // Scale and pan are one state, not two. A trackpad fires several wheel events
  // inside a single frame: with two states, each event reads a `scale` React has
  // not re-rendered yet, and all but the first are thrown away — the figure
  // hops one step and stops. One state means every gesture is computed from the
  // value the previous one produced.
  const [view, setView] = useState<View>(FITTED);
  const stageRef = useRef<HTMLDivElement>(null);
  // Live pointers, so one finger pans and two pinch. A ref rather than state:
  // these change on every move event and nothing renders off them directly.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; scale: number } | null>(null);
  const panFrom = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  // The gesture handlers need the scale as it stands *now* to decide whether a
  // zoom-out is a dismiss, and `view` in their closure is a render behind.
  const viewRef = useRef<View>(FITTED);
  viewRef.current = view;
  // A tap in progress, and whether it has already moved far enough to be a drag.
  const tap = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  // A tap waiting to see whether a second one follows. Cleared by the second tap,
  // which zooms instead of closing.
  const pendingTap = useRef<number | null>(null);
  const pinched = useRef(false);

  const open = figure !== null;

  // What this particular figure can be *done to* — see figure-interaction.ts.
  // The turn and the loupe are reachable only here, because in the flow of a
  // question those figures are an `<img>`, which receives no pointer events at
  // all. A chart is the exception: it is inlined into the question too, so its
  // highlight is available without opening anything (see FigureChart).
  const mode = useMemo(() => (figure ? interactionFor(figure.src) : null), [figure?.src]);
  const axis = useMemo(() => (figure ? axisFor(figure.src) : null), [figure?.src]);
  // Degrees away from the pose the question drew. A figure always opens at that
  // pose — the first thing a learner sees is the drawing they were reading — and
  // always comes back to it, so this is never anything but transient.
  const [offset, setOffset] = useState(0);
  // Read by the return animation, which needs the value as it stands now rather
  // than the one its closure captured — the same reason `viewRef` exists.
  const offsetRef = useRef(0);
  offsetRef.current = offset;
  const offsetFrom = useRef(0);
  const returning = useRef<number | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // The loupe's centre, as a fraction of the rendered image. Null while nobody
  // is pointing at the figure.
  const [lens, setLens] = useState<{ x: number; y: number } | null>(null);
  // The bar or slice being pointed at, said in words. A highlight on its own asks
  // the child to read the value back off the axis, which is the work the highlight
  // was meant to help with.
  const [picked, setPicked] = useState<{ label: string; value: string } | null>(null);

  const base = figure?.src ?? "";
  useEffect(() => {
    setOffset(0);
    setLens(null);
    setPicked(null);
  }, [base]);

  const shownSrc = base;

  const cancelPendingTap = useCallback(() => {
    if (pendingTap.current === null) return;
    window.clearTimeout(pendingTap.current);
    pendingTap.current = null;
  }, []);

  // Nothing may fire onto a closed figure — a close scheduled by a tap and a
  // close by any other route would otherwise both run.
  useEffect(() => cancelPendingTap, [cancelPendingTap]);

  // A newly opened figure starts fitted, never at the last figure's zoom.
  useEffect(() => {
    if (open) setView(FITTED);
  }, [open, figure?.src]);

  /** Panning is bounded by how much of the figure is off-screen, or a drag can
      lose it entirely and the child is left staring at a backdrop. */
  const clampOffset = useCallback((x: number, y: number, atScale: number) => {
    const stage = stageRef.current;
    if (!stage) return { x, y };
    const limitX = (stage.clientWidth * (atScale - 1)) / 2;
    const limitY = (stage.clientHeight * (atScale - 1)) / 2;
    return { x: clamp(x, -limitX, limitX), y: clamp(y, -limitY, limitY) };
  }, []);

  /**
   * `next` may be a factor-style function so a gesture composes with whatever
   * the last one left, and `anchor` (relative to the stage's centre) keeps
   * whatever sits under the cursor where it is — zooming into the corner of a
   * chart otherwise throws that corner off the screen.
   */
  const zoom = useCallback(
    (next: number | ((current: number) => number), anchor?: { x: number; y: number }) => {
      setView((v) => {
        const target = clamp(typeof next === "function" ? next(v.scale) : next, FIT, MAX_SCALE);
        if (target === v.scale) return v;
        if (target === FIT) return FITTED;
        const a = anchor ?? { x: 0, y: 0 };
        const ratio = target / v.scale;
        const moved = clampOffset(a.x - (a.x - v.x) * ratio, a.y - (a.y - v.y) * ratio, target);
        return { scale: target, ...moved };
      });
    },
    [clampOffset]
  );

  const dismiss = useCallback(() => {
    cancelPendingTap();
    onClose();
  }, [cancelPendingTap, onClose]);

  // Esc closes, +/- zoom, 0 resets — a keyboard user never has to reach for the
  // mouse to get back out of a figure.
  //
  // On `document`, in the CAPTURE phase, deliberately. A figure is opened from
  // inside the question, where the answer input and the choice buttons have
  // their own key handlers: React calls `stopPropagation` on the *native* event
  // from its root-container listener, so a handler between the figure and the
  // window can swallow Escape before a bubble-phase listener ever sees it.
  // Capturing at the document means nothing downstream can take it away.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" || e.key === "Esc") {
        e.preventDefault();
        e.stopPropagation();
        dismiss();
      } else if (e.key === "+" || e.key === "=") {
        zoom((s) => s + STEP);
      } else if (e.key === "-" || e.key === "_") {
        zoom((s) => s - STEP);
      } else if (e.key === "0") {
        zoom(FIT);
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, dismiss, zoom]);

  // The page behind must not scroll while a figure is open: on a phone the
  // scroll would otherwise carry the question away under the overlay.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Wheel zoom has to be a non-passive listener or the browser scrolls the page
  // instead; React's onWheel is registered passive.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !open) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      // Already fitted and still zooming out: there is nothing smaller to show,
      // and "back out" is what the gesture means. Closing is the honest answer —
      // the alternative is a figure that absorbs the scroll and does nothing.
      if (e.deltaY > 0 && viewRef.current.scale <= FIT) {
        dismiss();
        return;
      }
      const rect = stage!.getBoundingClientRect();
      const anchor = {
        x: e.clientX - rect.left - rect.width / 2,
        y: e.clientY - rect.top - rect.height / 2,
      };
      zoom((s) => s * (e.deltaY < 0 ? WHEEL_FACTOR : 1 / WHEEL_FACTOR), anchor);
    }
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, [open, zoom, dismiss]);

  function onPointerDown(e: React.PointerEvent) {
    // A primary pointer down is the start of a fresh gesture, so anything still
    // in the map is a finger whose `pointerup` never arrived. Left there it
    // would be counted forever and every later drag would read as a pinch.
    if (e.isPrimary) {
      pointers.current.clear();
      pinched.current = false;
      tap.current = { x: e.clientX, y: e.clientY, moved: false };
    }
    // Registered before the capture, which is a convenience (a drag that leaves
    // the figure keeps panning) and is allowed to fail — losing the gesture
    // because the capture was refused would be the worse trade.
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try {
      (e.target as Element).setPointerCapture?.(e.pointerId);
    } catch {
      /* not capturable — panning still works while the pointer is over the stage */
    }
    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: view.scale };
      pinched.current = true;
      tap.current = null;
      panFrom.current = null;
    } else if (pointers.current.size === 1) {
      panFrom.current = { x: e.clientX, y: e.clientY, ox: view.x, oy: view.y };
      offsetFrom.current = offset;
      if (returning.current !== null) {
        cancelAnimationFrame(returning.current);
        returning.current = null;
      }
      if (mode === "loupe") setLens(lensAt(e));
    }
  }

  /** Pointer position inside the rendered image, for the loupe. */
  function lensAt(e: { clientX: number; clientY: number }) {
    const img = imgRef.current;
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null;
    return { x: x / rect.width, y: y / rect.height };
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (tap.current && Math.hypot(e.clientX - tap.current.x, e.clientY - tap.current.y) > TAP_SLOP) {
      tap.current.moved = true;
    }

    if (pointers.current.size >= 2 && pinch.current) {
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const base = pinch.current;
      // Pinching in while already fitted is the wheel case above with fingers.
      if (base.scale <= FIT && dist < base.dist * PINCH_CLOSE) {
        dismiss();
        return;
      }
      if (base.dist > 0) zoom((base.scale * dist) / base.dist);
      return;
    }
    // Panning only means something once there is more figure than screen.
    const from = panFrom.current;
    if (!from) return;
    const dx = e.clientX - from.x;
    const dy = e.clientY - from.y;

    // A turnable solid spends the drag on turning instead of panning, and the
    // swipe-down dismissal is given up with it: a learner turning a cube
    // downwards must not have the figure vanish underneath them. Every other way
    // out still works — tap, Esc, zoom out, the X.
    if (mode === "peek" && axis && viewRef.current.scale <= FIT) {
      setOffset(offsetFromDrag(offsetFrom.current, dx, dy, axis));
      return;
    }
    if (mode === "loupe" && viewRef.current.scale <= FIT) {
      setLens(lensAt(e));
      return;
    }
    // Nothing to pan at fit, so a downward drag there is free to mean "away" —
    // the gesture every photo viewer on a phone already answers to. Only
    // downward, and only when it is more down than sideways.
    if (viewRef.current.scale <= FIT) {
      if (!mode && dy > SWIPE_CLOSE && dy > Math.abs(dx)) dismiss();
      return;
    }
    setView((v) => (v.scale <= FIT ? v : { ...v, ...clampOffset(from.ox + dx, from.oy + dy, v.scale) }));
  }

  function onPointerUp(e: React.PointerEvent) {
    const wasSingle = pointers.current.size === 1;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) panFrom.current = null;

    const finished = tap.current;
    tap.current = null;
    // A tap on a bar or a slice picks it. Closing on that tap as well would mean
    // the highlight could never be seen on a touch screen, where there is no
    // hover to reach it with.
    const onPart = mode === "chart" && !!(e.target as Element | null)?.closest?.("[data-part]");
    if (onPart) return;
    // The loupe is held, not parked: it follows a finger and leaves with it.
    // Left on screen it would cover the very mark it was opened to read.
    if (mode === "loupe" && pointers.current.size === 0) setLens(null);
    if (mode === "peek" && pointers.current.size === 0) springBack();
    if (wasSingle && !pinched.current && finished && !finished.moved) {
      onTap({ clientX: e.clientX, clientY: e.clientY });
    }
  }

  /**
   * Back to the pose the question drew, over RETURN_MS.
   *
   * Never a jump. The figure was turned by hand, so snapping it back reads as the
   * drawing being *replaced* rather than as it settling — and the pose it returns
   * to is the one the sentence was written about, which is why it returns at all
   * rather than staying where it was left.
   */
  function springBack() {
    if (returning.current !== null) cancelAnimationFrame(returning.current);
    const from = offsetRef.current;
    if (from === 0) return;
    const startedAt = performance.now();

    function frame(now: number) {
      const t = Math.min(1, (now - startedAt) / RETURN_MS);
      setOffset(from * (1 - easeOutCubic(t)));
      if (t < 1) returning.current = requestAnimationFrame(frame);
      else {
        returning.current = null;
        setOffset(0);
      }
    }
    returning.current = requestAnimationFrame(frame);
  }

  /** A cancelled gesture is not a tap: the pointer was taken away rather than
      lifted, and closing on it would dismiss a figure nobody let go of. */
  function onPointerCancel(e: React.PointerEvent) {
    tap.current = null;
    onPointerUp(e);
  }

  /**
   * A tap closes; two taps zoom. The close waits `DOUBLE_TAP_MS` for its
   * partner, which is what lets both live on the same gesture — and the wait is
   * spent on a dismissal, where a quarter second is not felt, rather than on the
   * zoom, where it would be.
   */
  function onTap(at: { clientX: number; clientY: number }) {
    if (pendingTap.current !== null) {
      cancelPendingTap();
      zoom(viewRef.current.scale > FIT ? FIT : DOUBLE_TAP_SCALE, anchorOf(at));
      return;
    }
    pendingTap.current = window.setTimeout(() => {
      pendingTap.current = null;
      onClose();
    }, DOUBLE_TAP_MS);
  }

  function anchorOf(e: { clientX: number; clientY: number }) {
    const rect = stageRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left - rect.width / 2, y: e.clientY - rect.top - rect.height / 2 };
  }

  if (!open || !figure) return null;

  // Measured once per render rather than six times inline. Both boxes are read
  // from the live layout because the figure's size depends on which side it
  // fills, which is only known after it has loaded.
  const loupeStyle = (() => {
    if (mode !== "loupe" || !lens || !imgRef.current || !stageRef.current) return null;
    const img = imgRef.current.getBoundingClientRect();
    const stage = stageRef.current.getBoundingClientRect();
    const cx = img.left - stage.left + lens.x * img.width;
    const cy = img.top - stage.top + lens.y * img.height;
    return {
      transform: `translate(${cx - LOUPE_R}px, ${cy - LOUPE_R}px)`,
      width: LOUPE_R * 2,
      height: LOUPE_R * 2,
      backgroundImage: `url("${shownSrc}")`,
      backgroundRepeat: "no-repeat",
      backgroundSize: `${img.width * LOUPE_ZOOM}px ${img.height * LOUPE_ZOOM}px`,
      backgroundPosition: `${LOUPE_R - lens.x * img.width * LOUPE_ZOOM}px ${
        LOUPE_R - lens.y * img.height * LOUPE_ZOOM
      }px`,
    } as React.CSSProperties;
  })();

  return createPortal(
    <div
      className="figure-lightbox"
      // The backdrop closes: the page behind is visible, so clicking it is
      // "back to the question", which is what a modal's outside means.
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div
        className="figure-lightbox__panel"
        role="dialog"
        aria-modal="true"
        aria-label={figure.alt || "Padidintas paveikslėlis"}
      >
        <div className="figure-lightbox__bar">
          <div className="figure-lightbox__zoom">
            <button
              type="button"
              aria-label="Mažinti"
              onClick={() => zoom((s) => s - STEP)}
              disabled={view.scale <= FIT}
            >
              <Minus className="h-5 w-5" />
            </button>
            <span className="figure-lightbox__level">{Math.round(view.scale * 100)}%</span>
            <button
              type="button"
              aria-label="Didinti"
              onClick={() => zoom((s) => s + STEP)}
              disabled={view.scale >= MAX_SCALE}
            >
              <Plus className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Atstatyti"
              onClick={() => zoom(FIT)}
              disabled={view.scale === FIT}
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>
          <button type="button" className="figure-lightbox__close" aria-label="Užverti" onClick={dismiss}>
            <X className="h-6 w-6" />
          </button>
        </div>

        <div
          ref={stageRef}
          className="figure-lightbox__stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          style={{
            cursor:
              view.scale > FIT
                ? "grab"
                : mode === "peek"
                  ? "grab"
                  : mode === "loupe"
                    ? "crosshair"
                    : mode === "chart"
                      ? "default"
                    : "zoom-out",
          }}
        >
          {mode === "chart" ? (
            <FigureChart
              src={base}
              alt={figure.alt}
              className="figure-lightbox__chart"
              style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
              onPicked={setPicked}
            />
          ) : mode === "peek" && axis ? (
            <FigurePoses
              src={base}
              alt={figure.alt}
              axis={axis}
              offset={offset}
              canvas={POSE_CANVAS}
              className="figure-lightbox__poses"
              style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
            />
          ) : (
          <img
            ref={imgRef}
            src={shownSrc}
            alt={figure.alt}
            className="figure-lightbox__img"
            draggable={false}
            style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
            // Which side to fill is the figure's own aspect against the screen's:
            // a ruler fills the width, a thermometer the height. Without this the
            // intrinsic size wins and a small figure opens just as small as it was.
            onLoad={(e) => {
              const img = e.currentTarget;
              const stage = stageRef.current;
              if (!stage || !img.naturalWidth || !img.naturalHeight) return;
              const figureAspect = img.naturalWidth / img.naturalHeight;
              const stageAspect = stage.clientWidth / stage.clientHeight;
              img.dataset.fit = figureAspect > stageAspect ? "width" : "height";
            }}
          />
          )}

          {/* The loupe. A round window on the same figure painted three times
              larger — which is lossless, these being SVG, so a 1° protractor tick
              becomes a real mark rather than a thicker blur. It is the answer to
              "where exactly is the arm pointing?", a question the whole figure
              enlarged does not answer any better, because the scale and the arm
              end up on opposite sides of the screen. */}
          {loupeStyle && <div aria-hidden="true" className="figure-lightbox__loupe" style={loupeStyle} />}
        </div>

        {/* Shown on a phone too, where it matters most: hover cannot teach a touch
            user that pinching works, and the space under a fitted figure is empty
            anyway. */}
        {/* What this figure can be done to comes first, because it is the thing
            nobody would guess; the zoom and the ways out are the same everywhere
            and go after it. Shown on a phone too, where it matters most: hover
            cannot teach a touch user that anything is draggable. */}
        <p className="figure-lightbox__hint">
          {mode === "peek" && (
            <span className="figure-lightbox__affordance">
              <Rotate3d className="h-4 w-4" aria-hidden="true" />
              {axis === "rotation"
                ? "Vilk į šoną — pamatysi kitą pusę, o atleidus figūra grįš"
                : "Vilk aukštyn arba žemyn — palenksi, o atleidus figūra grįš"}
            </span>
          )}
          {mode === "loupe" && (
            <span className="figure-lightbox__affordance">
              <Search className="h-4 w-4" aria-hidden="true" />
              Vilk per skalę — didinamasis stiklas eis paskui pirštą
            </span>
          )}
          {mode === "chart" && (
            <span className="figure-lightbox__affordance">
              <MousePointer className="h-4 w-4" aria-hidden="true" />
              {picked
                ? `${picked.label || "Pažymėta"}: ${picked.value}`
                : "Užvesk arba bakstelėk stulpelį — jis bus paryškintas"}
            </span>
          )}
          {figure.alt ? `${figure.alt} — ` : ""}
          Priartink dviem pirštais, ratuku arba bakstelėk du kartus. Užverti —
          bakstelėk{mode ? "" : ", nubrauk žemyn"}, atitolink arba paspausk Esc.
        </p>
      </div>
    </div>,
    document.body
  );
}
